// Wire and client-side contracts for the admin<->agent support chat.
//
// These types are the agent app's half of a contract the admin app mirrors. Two
// invariants are load-bearing and are easy to lose in a refactor:
//
//  1. Ordering keys off `sequence`, never `createdAt`. Phone clocks are wrong, and two
//     messages written in the same millisecond sort non-deterministically. Backfill
//     cursors and dedupe use it too.
//  2. `clientId` is minted once, on first compose, and replayed on every retry. It is
//     the whole idempotency story: an agent on a flaky link WILL retry a send whose
//     response was lost, and a duplicate support message is a duplicate instruction.

/**
 * Lifecycle of an outgoing message.
 *
 * `queued` and `sending` are client-only. `failed` is terminal for one attempt, not for
 * the message — the outbox will try again unless it has been held.
 *
 * `delivered` must mean the recipient's client actually acknowledged. If the server
 * cannot distinguish it from `sent` it reports null and the UI shows one tick forever;
 * showing two ticks we cannot back up is worse than showing one.
 */
export type SupportMessageState = 'queued' | 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

export type SupportMessageKind = 'text' | 'image' | 'document' | 'system' | 'deleted';

/**
 * Which side of a conversation a message came from, per the server.
 *
 * <p>`agent` | `desk` on a support thread; `parent` | `child` on an agent↔agent thread. Always
 * derivable from the thread key alone, which is what makes it reload-proof. It is NOT an
 * ownership test on its own once two agents share a thread — see {@link IOwnershipContext}.</p>
 */
export type SupportSenderSide = 'agent' | 'desk' | 'parent' | 'child';

/** The kinds of conversation the one chat engine drives. */
export type ChatThreadKind = 'support' | 'peer';

/**
 * Everything needed to decide whether a message is the reader's own.
 *
 * <p>`senderId === me` is the primary test and the only one that works when both participants
 * are agents. `senderSide === 'agent'` is the fallback for support threads talking to a server
 * that never fills `senderCoreUserId` (every api.vesr build before peer chat). `ownClientIds`
 * covers a message that has not round-tripped yet.</p>
 */
export interface IOwnershipContext {
	/** The reader's core user id. 0 when unknown. */
	me: number;
	kind: ChatThreadKind;
	ownClientIds?: Set<string>;
}

/**
 * The other party in a conversation, as the UI should name them.
 *
 * <p>The support desk is a FUNCTION, not a person — admins rotate through it — so its copy says
 * "the support desk". A peer is a named colleague. Copy derived from this lives in
 * `lib/chat-counterpart-copy.ts` so the composer, transcript, quote and tombstone agree.</p>
 */
export interface ChatCounterpart {
	kind: 'desk' | 'peer';
	/** Display name: "Support desk", or the person's full name. */
	name: string;
	/** "Polling-unit agent", "Ward agent"… shown beside the name where there is room. */
	roleLabel?: string;
}

export const DESK_COUNTERPART: ChatCounterpart = { kind: 'desk', name: 'Support desk' };

export interface ISupportAttachment {
	id?: string;
	fileName: string;
	fileUrl?: string;
	thumbnailUrl?: string;
	contentType: string;
	fileSizeBytes: number;
}

/**
 * Enough of a quoted message to draw it, as the server resolved it.
 *
 * <p>Resolved server-side AT READ TIME, not snapshotted when the reply was sent. WhatsApp and
 * Telegram freeze the quoted text so a quote outlives a deletion; here delete-for-everyone is a
 * retraction, and a frozen snapshot would leave a withdrawn instruction legible inside every
 * reply that quoted it. So `isDeleted` arrives with an empty `body`.</p>
 *
 * <p>Mirrors `ISupportReplyContext` in admin-frontend/src/app/support/types/support.types.ts.</p>
 */
export interface ISupportReplyContext {
	id: string;
	/** The parent's place in the thread — how we decide whether we can scroll to it. */
	sequence: number;
	senderSide: SupportSenderSide;
	/** Who wrote the quoted message. Absent on servers that predate agent↔agent threads. */
	senderCoreUserId?: number | null;
	kind: SupportMessageKind;
	/** Trimmed to 140 by the server. Empty when the parent was retracted, or carried only photos. */
	body: string;
	/** Lets a quote of a wordless photo say so instead of rendering a blank line. */
	hasAttachments: boolean;
	isDeleted: boolean;
	createdAt: string;
}

export interface ISupportMessage {
	/** Server id. Absent while the message is still client-only. */
	id?: string;
	/** Client-minted, stable across retries. Present on every message this device sent. */
	clientId?: string;
	threadId: string;
	senderId: number;
	senderName?: string;
	/**
	 * Which side sent it, per the server, which rebuilds the agent's deterministic GUID
	 * from the thread key. Authoritative and reload-proof; absent until the message has
	 * round-tripped.
	 */
	senderSide?: SupportSenderSide;
	/** Monotonic per thread. The sort key — see the note at the top of this file. */
	sequence: number;
	kind: SupportMessageKind;
	body: string;
	attachments?: ISupportAttachment[];
	state: SupportMessageState;
	createdAt: string;
	editedAt?: string | null;
	/** Read by someone other than the sender. Survives a reload; drives the filled tick. */
	readByOther?: boolean;
	deletedAt?: string | null;
	/** Who retracted it, so the tombstone can name the desk rather than a person. */
	deletedBy?: 'self' | SupportSenderSide | null;
	/**
	 * The message this one answers.
	 *
	 * <p>`replyToId` set with `replyTo` absent is a THIRD state — replied to something that no
	 * longer resolves — and must not render like "not a reply". `undefined` on both means the
	 * server predates the feature.</p>
	 */
	replyToId?: string | null;
	replyTo?: ISupportReplyContext | null;
	/**
	 * The election result this message is about — set on a Result Verification "Request Info"
	 * and on every reply to one (the server copies it onto replies). Null or absent otherwise.
	 */
	context?: ISupportMessageContext | null;
}

/** A message's link to one result, as api.vesr sends it. */
export interface ISupportMessageContext {
	resultId: string;
	/** EC8A–EC8D. */
	level: string;
	entityCode: string | null;
}

/**
 * Attachment metadata as it survives in the outbox.
 *
 * The blob is deliberately NOT here. localStorage is ~5MB, synchronous and string-only,
 * and a 3MB photo base64-encodes to ~4MB — persisting it would blow the quota, freeze
 * the main thread, and evict the queued *text* messages alongside it. The compressed
 * File lives in an in-memory Map owned by the chat hook; only this metadata plus a tiny
 * thumbnail data URL is written to storage, so the bubble still renders after a reload
 * and the agent can re-pick the photo against the same clientId.
 */
export interface IOutboxAttachmentMeta {
	fileName: string;
	contentType: string;
	fileSizeBytes: number;
	/** ~4KB JPEG, so a reloaded tab can still draw the bubble. */
	thumbnailDataUrl?: string;
}

export type OutboxItemState = 'queued' | 'sending' | 'failed' | 'held';

export interface IOutboxItem {
	/** Minted once at enqueue. Replayed verbatim on every attempt. */
	clientId: string;
	/** The server thread key, when it was known at enqueue time. Empty otherwise. */
	threadId: string;
	/**
	 * Which conversation this belongs to, as a LOCAL id (`support`, or `peer:{election}:{user}`).
	 *
	 * <p>The server key is unknown until `POST threads` succeeds, and the whole point of the
	 * outbox is to hold a message before that. Items written before this field existed carry no
	 * value and belong to the support thread — the only conversation that existed then.</p>
	 */
	threadRef?: string;
	body: string;
	attachment?: IOutboxAttachmentMeta;
	createdAt: string;
	/** Incremented per attempt. Drives the backoff ladder and the held cutoff. */
	attempts: number;
	/** Epoch ms. The flusher skips items whose time has not come. */
	nextAttemptAt: number;
	state: OutboxItemState;
	lastError?: string;
	/**
	 * The reply target, persisted with the item rather than held in component state.
	 *
	 * <p>The outbox survives a reload and a retry may happen minutes later on a different app
	 * launch. A reply target kept only in memory would be gone by then, and the message would
	 * eventually send answering nothing — the anchor silently dropped on exactly the flaky link
	 * the outbox exists for.</p>
	 */
	replyToId?: string;
	/** The quote to draw on the queued bubble, until the server's own resolved copy arrives. */
	replyTo?: ISupportReplyContext | null;
}
